import { useState } from "react";
import type { Tag } from "../types";
import TagListItem from "./tag-list-item";
import { DeleteConfirmationModal } from "../../review/components/delete-confirmation-modal";
import TagListSkeleton from "./tag-list-skeleton";

export interface TagListProps {
	tags: Tag[];
	workspaceId: string;
	className?: string;
	onDelete?: (id: string, workspaceId: string) => void;
	onEdit?: (tag: Tag) => void;
	isLoading?: boolean;
	hasMore?: boolean;
	isLoadingMore?: boolean;
	onLoadMore?: () => void;
}

export default function TagList({ tags, workspaceId, className = "", onDelete, onEdit, isLoading = false, hasMore = false, isLoadingMore = false, onLoadMore }: TagListProps) {
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const handleDeleteClick = (id: string) => {
		const tag = tags.find((t) => t.id === id);
		if (tag) {
			setSelectedTag(tag);
			setDeleteModalOpen(true);
		}
	};

	const handleDeleteConfirm = async () => {
		if (!selectedTag || !onDelete) return;

		setIsDeleting(true);
		try {
			await onDelete(selectedTag.id, workspaceId);
			setDeleteModalOpen(false);
			setSelectedTag(null);
		} catch (error) {
			console.error("Failed to delete tag:", error);
			alert("Failed to delete tag. Please try again.");
		} finally {
			setIsDeleting(false);
		}
	};

	if (isLoading) {
		return <TagListSkeleton />;
	}

	return (
		<>
			<div className={`space-y-2 ${className}`}>
				{tags.length > 0 ? (
					tags.map((tag, index) => <TagListItem key={`${tag.id}-${index}`} tag={tag} workspaceId={workspaceId} onDelete={onDelete ? handleDeleteClick : undefined} onEdit={onEdit} />)
				) : (
					<div className="text-center py-12 text-gray-500">
						<p>No tags found</p>
					</div>
				)}
			</div>

			{/* Load More Button */}
			{hasMore && onLoadMore && (
				<div className="flex justify-center mt-8">
					<button
						type="button"
						onClick={onLoadMore}
						disabled={isLoadingMore}
						className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-tertiary bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isLoadingMore ? (
							<>
								<svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
									<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									/>
								</svg>
								Loading...
							</>
						) : (
							"Load More"
						)}
					</button>
				</div>
			)}

			<DeleteConfirmationModal
				open={deleteModalOpen}
				title="Delete Tag"
				description={`Are you sure you want to delete the tag "${selectedTag?.label}"? This action cannot be undone.`}
				isLoading={isDeleting}
				onOpenChange={setDeleteModalOpen}
				onConfirm={handleDeleteConfirm}
			/>
		</>
	);
}
